/**
 * Page arithmetic shared by the printable organizers (Frayer model, T-chart, storyboard).
 *
 * A sheet prints with the margins `worksheetPrintCss` sets (0.5 in on Letter, 12 mm on A4).
 * The organizer's drawing area is the page inside those margins, less a strip at the top for
 * the title and the name line, and its proportions are an `aspect-ratio` so that the same
 * rule gives the right height on screen and on paper.
 */

export type Paper = "letter" | "a4";
export type Direction = "landscape" | "portrait";

const PAGE_MM: Record<
  Paper,
  { width: number; height: number; margin: number }
> = {
  letter: { width: 215.9, height: 279.4, margin: 12.7 },
  a4: { width: 210, height: 297, margin: 12 },
};

/** Room kept above the drawing area for the title and name line, in millimetres. */
export const HEADER_MM = 20;

/** Width of the printable area, in millimetres. */
export function usableWidth(paper: Paper, direction: Direction): number {
  const page = PAGE_MM[paper];
  const across = direction === "landscape" ? page.height : page.width;
  return across - 2 * page.margin;
}

/** Height of the printable area, in millimetres. */
export function usableHeight(paper: Paper, direction: Direction): number {
  const page = PAGE_MM[paper];
  const down = direction === "landscape" ? page.width : page.height;
  return down - 2 * page.margin;
}

/**
 * Width divided by height of the drawing area under a header of `header` millimetres.
 * Rounded to three places so server and client render the same style text.
 */
export function drawingRatio(
  paper: Paper,
  direction: Direction,
  header: number = HEADER_MM
): number {
  const ratio =
    usableWidth(paper, direction) / (usableHeight(paper, direction) - header);
  return Math.round(ratio * 1000) / 1000;
}
